// @wc-ignore-file
/**
 * CR-239 S6: the transport of the CR "Linear issues" section (contract:
 * shared/types/linear-context.ts). Mirror of jira-context-api.ts.
 *
 * - `linear-mappings`: the project's enabled mappings as a member may see
 *   them (members cannot read integration_contexts — owner/admin RLS).
 * - `linear-links`: the CR's links, the stale states refreshed by the server
 *   first. The section renders the direct database read and lets this answer
 *   replace it (change-requests-hooks.ts).
 * - `linear-send`: create / update the parent issue. CR-274: the dialog shows
 *   no preview, so the frontend never asks for the server's `dryRun`.
 * - Read the error body's `code` FIRST: the HTTP status is not a discriminant
 *   (409 is `existing_links`, `send_in_progress`, `organization_mismatch` and
 *   `linear_not_configured`).
 */
import { invokeFunction } from "@/lib/functions";
import type {
  CrLinearIssueLink,
  LinearContextErrorBody,
  LinearExistingLinksBody,
  LinearLinksSuccessBody,
  LinearMappingOption,
  LinearMappingsSuccessBody,
  LinearSendRequest,
  LinearSendSuccessBody,
  LinearSyncChildrenSuccessBody,
} from "@shared/types/linear-context";

/** A route error code (never `existing_links`, which is an outcome), or `network_error`. */
export type LinearClientErrorCode = LinearContextErrorBody["code"] | "network_error";

export class LinearContextError extends Error {
  readonly code: LinearClientErrorCode;
  readonly retryAfterSeconds?: number;

  constructor(code: LinearClientErrorCode, retryAfterSeconds?: number) {
    super(code);
    this.name = "LinearContextError";
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

// A Record over the code union: a new route code fails typecheck here until it
// is listed (a missing one would silently read as `linear_unavailable`).
const ROUTE_CODE_FLAGS = {
  invalid_request: true,
  forbidden: true,
  cr_not_found: true,
  mapping_not_found: true,
  mapping_disabled: true,
  link_not_found: true,
  send_in_progress: true,
  breakdown_unavailable: true,
  organization_mismatch: true,
  linear_not_configured: true,
  linear_reconnect_required: true,
  linear_not_found: true,
  linear_rate_limited: true,
  linear_invalid_request: true,
  linear_unavailable: true,
} satisfies Record<LinearContextErrorBody["code"], true>;
const ROUTE_CODES: ReadonlySet<string> = new Set(Object.keys(ROUTE_CODE_FLAGS));

/**
 * Map an invokeFunction error to a stable code. The membership middleware
 * answers `{ error }` without a code, so its refusal is read from the status.
 */
export function toLinearContextError(error: {
  status?: number;
  body?: unknown;
}): LinearContextError {
  const body = error.body as Partial<LinearContextErrorBody> | null | undefined;
  if (body && typeof body.code === "string" && ROUTE_CODES.has(body.code)) {
    const retryAfter =
      typeof body.retryAfterSeconds === "number" && Number.isFinite(body.retryAfterSeconds)
        ? body.retryAfterSeconds
        : undefined;
    return new LinearContextError(body.code as LinearClientErrorCode, retryAfter);
  }
  if (error.status === undefined) return new LinearContextError("network_error");
  if (error.status === 401 || error.status === 403) return new LinearContextError("forbidden");
  if (error.status === 400) return new LinearContextError("invalid_request");
  return new LinearContextError("linear_unavailable");
}

/** The mappings answer: the enabled mappings and whether Linear is connected (CR-268). */
export interface LinearMappingsAnswer {
  mappings: LinearMappingOption[];
  connected: boolean;
}

/** The project's enabled Linear mappings ([] = nothing to send with) and its connection state. */
export async function fetchLinearMappings(containerId: string): Promise<LinearMappingsAnswer> {
  const { data, error } = await invokeFunction<LinearMappingsSuccessBody>("linear-mappings", {
    body: { containerId },
  });
  if (error || !data) throw toLinearContextError(error ?? {});
  // An api-server older than CR-268 sends no flag: treat it as connected.
  return { mappings: data.mappings, connected: data.connected !== false };
}

/** The CR's links after the server refreshed the stale states. */
export async function fetchLinearLinks(request: {
  containerId: string;
  crId: string;
}): Promise<Pick<LinearLinksSuccessBody, "links" | "refreshed">> {
  const { data, error } = await invokeFunction<LinearLinksSuccessBody>("linear-links", {
    body: request,
  });
  if (error || !data) throw toLinearContextError(error ?? {});
  return { links: data.links, refreshed: data.refreshed };
}

/** What the dialog sends: the request without the server-only fields (actor, dryRun). */
export type LinearSendInput = Omit<LinearSendRequest, "actorId" | "dryRun">;

/** A create that wrote nothing because the CR already has parent issues (the 409 step). */
interface LinearSendExistingLinks {
  status: "existing_links";
  existing: CrLinearIssueLink[];
}

export type LinearSendOutcome =
  | Pick<LinearSendSuccessBody, "status" | "parentLink" | "warnings" | "children">
  | LinearSendExistingLinks;

/**
 * Create or update the parent issue. `existing_links` is returned, not thrown:
 * it is an expected outcome the dialog answers with Update / Create another.
 */
export async function sendToLinear(request: LinearSendInput): Promise<LinearSendOutcome> {
  const { data, error } = await invokeFunction<LinearSendSuccessBody>("linear-send", {
    body: request,
  });
  if (error) {
    const body = error.body as Partial<LinearExistingLinksBody> | null | undefined;
    if (body?.code === "existing_links" && Array.isArray(body.existing)) {
      return { status: "existing_links", existing: body.existing };
    }
    throw toLinearContextError(error);
  }
  if (!data) throw new LinearContextError("linear_unavailable");
  return {
    status: data.status,
    parentLink: data.parentLink,
    warnings: data.warnings,
    children: data.children,
  };
}

/** Sync the Technical Plan tasks as sub-issues of an existing parent link (S7). */
export async function syncLinearChildren(request: {
  containerId: string;
  crId: string;
  parentLinkId: string;
}): Promise<LinearSyncChildrenSuccessBody["children"]> {
  const { data, error } = await invokeFunction<LinearSyncChildrenSuccessBody>(
    "linear-sync-children",
    { body: request },
  );
  if (error || !data) throw toLinearContextError(error ?? {});
  return data.children;
}

/**
 * A Linear issue link the page may render as an href, or null: https on
 * linear.app only, no credentials or port. The server builds the URL from the
 * organization's validated url key (and a CHECK pins its shape); this is the
 * belt to those braces, so a stored value can never become a `javascript:` link.
 */
export function safeLinearUrl(url: string | null | undefined): string | null {
  if (typeof url !== "string") return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") return null;
    if (parsed.username || parsed.password || parsed.port) return null;
    if (parsed.hostname.toLowerCase() !== "linear.app") return null;
    return parsed.href;
  } catch {
    return null;
  }
}
